/**
 * usePlayback - Simple playback state management
 * 
 * Just manages play/pause state and position.
 * The actual video player drives position updates.
 */
import { useState, useCallback, useRef } from 'react';
import { getEffectiveDuration } from '../utils/clipUtils';

export function usePlayback(videoClips, audioTracks, totalDuration, scenes) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playheadPosition, setPlayheadPosition] = useState(0);
  const positionRef = useRef(0);

  // Find clip at a given position
  const getClipAtPosition = useCallback((positionMs) => {
    if (!videoClips?.length) return null;
    return videoClips.find(clip => {
      const effectiveStart = clip.start_ms;
      const effectiveDuration = getEffectiveDuration(clip);
      return positionMs >= effectiveStart && positionMs < effectiveStart + effectiveDuration;
    });
  }, [videoClips]);

  // Get clip index at position
  const getClipIndexAtPosition = useCallback((positionMs) => {
    if (!videoClips?.length) return -1;
    return videoClips.findIndex(clip => {
      const effectiveStart = clip.start_ms;
      const effectiveDuration = getEffectiveDuration(clip);
      return positionMs >= effectiveStart && positionMs < effectiveStart + effectiveDuration;
    });
  }, [videoClips]);

  // Get current position
  const getPosition = useCallback(() => positionRef.current, []);

  // Play
  const play = useCallback(() => {
    if (positionRef.current >= totalDuration) {
      positionRef.current = 0;
      setPlayheadPosition(0);
    }
    setIsPlaying(true);
  }, [totalDuration]);

  // Pause
  const pause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // Toggle
  const togglePlayPause = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  // Seek - called by video player or timeline
  const seek = useCallback((positionMs) => {
    const clamped = Math.max(0, Math.min(positionMs, totalDuration));
    positionRef.current = clamped;
    setPlayheadPosition(clamped);
    
    // Stop at end
    if (clamped >= totalDuration) {
      setIsPlaying(false);
    }
  }, [totalDuration]);

  // Frame stepping
  const stepForward = useCallback(() => {
    seek(positionRef.current + (1000 / 24));
  }, [seek]);

  const stepBackward = useCallback(() => {
    seek(positionRef.current - (1000 / 24));
  }, [seek]);

  const goToStart = useCallback(() => seek(0), [seek]);
  const goToEnd = useCallback(() => seek(totalDuration), [seek, totalDuration]);
  const skipForward = useCallback((amt = 5000) => seek(positionRef.current + amt), [seek]);
  const skipBackward = useCallback((amt = 5000) => seek(positionRef.current - amt), [seek]);

  return {
    isPlaying,
    playheadPosition,
    getPosition,
    play,
    pause,
    togglePlayPause,
    seek,
    stepForward,
    stepBackward,
    goToStart,
    goToEnd,
    skipForward,
    skipBackward,
    getClipAtPosition,
    getClipIndexAtPosition
  };
}

export default usePlayback;
